import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Import and configure dayjs for MUI
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

// Layout Components
import Layout from './components/Layout/Layout';

// Pages
import Dashboard from './pages/Dashboard/Dashboard';
import PermitList from './pages/Permits/PermitList';
import PermitDetail from './pages/Permits/PermitDetail';
import PermitWizard from './pages/Permits/PermitWizard';
import StationLog from './pages/StationLog/StationLog';
import SafetyDashboard from './pages/Safety/SafetyDashboard';

import Reports from './pages/Reports/Reports';
import Login from './pages/Auth/Login';
import PermitForm from './pages/Permits/PermitForm';
import PermitComprehensiveForm from './pages/Permits/PermitComprehensiveForm';
import AccessPermitPage from './pages/Permits/AccessPermitPage';
import PlantInformation from './pages/PlantInformation/PlantInformation';
import PlannedWorks from './pages/PlannedWorks/PlannedWorks';
import { PlannedWorkDetail } from './pages/PlannedWorks';
import { DocumentsDashboard } from './pages/Documents';
import { CamerasDashboard } from './pages/Cameras/CamerasDashboard';
import HistorianPage from './pages/SCADA/HistorianPage';
import { AlarmDashboard } from './pages/Alarms';
import { AlarmHistory } from './pages/Alarms/AlarmHistory';
import { UserSettings } from './components/UserSettings/UserSettings';

// Maintenance Pages
import {
  MaintenanceDashboard,
  MaintenanceWorkOrders,
  MaintenanceSchedules,
  MaintenanceRoutes,
  MaintenanceTaskDefinitions,
  StandardTaskManager,
} from './pages/Maintenance';
import EnhancedMaintenanceDashboard from './pages/Maintenance/EnhancedMaintenanceDashboard';
import MaintenanceRecords from './pages/Maintenance/MaintenanceRecords';

// Hooks and Context
import { AuthProvider, useAuth } from './hooks/useAuth';
import { SiteProvider, useSiteContext } from './hooks/useSiteContext';
import { useRoleCheck } from './hooks/useAuth';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});


// Create Material-UI theme with Re-GenOps branding
const theme = createTheme({
  palette: {
    primary: {
      main: '#1e3a5f',  // Re-GenOps dark blue
      dark: '#152b45',
      light: '#3d5a7a',
    },
    secondary: {
      main: '#4caf50',  // Re-GenOps green (from checkmark)
      dark: '#388e3c',
      light: '#81c784',
    },
    success: {
      main: '#4caf50',  // Green for successful actions
      dark: '#388e3c',
      light: '#81c784',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e3a5f',
      secondary: '#5a6c7d',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#1e3a5f',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#1e3a5f',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
      color: '#1e3a5f',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
        },
      },
    },
  },
});

// Enhanced ProtectedRoute component with viewer restrictions
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowViewers?: boolean;
  requirePermission?: string;
}> = (props) => {
  const { children, allowViewers = false, requirePermission } = props;
  
  const { user, isAuthenticated, isLoading } = useAuth();
  const { isViewerOnly } = useRoleCheck();
  const { currentSite } = useSiteContext();
  

  // Show loading while auth is being checked
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if viewer is trying to access restricted area
  if (isViewerOnly() && !allowViewers) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check specific permissions if required
  if (requirePermission && user) {
    let hasPermission = false;
    
    // For permit creation, check site-specific permissions
    if (requirePermission === 'can_create_permits') {
      // If we have a current site, check site-specific permissions
      if (currentSite.siteId) {
        const siteRoles = user.site_roles?.filter((role: any) => role.site_id === currentSite.siteId);
        
        if (siteRoles && siteRoles.length > 0) {
          // User has site roles - check if any of them grant permit creation
          hasPermission = siteRoles.some((role: any) => 
            role.role === 'issuer' || role.effective_permissions?.can_create_permits === true
          );
        } else {
          // No site roles for current site - fall back to global permission
          hasPermission = Boolean(user[requirePermission as keyof typeof user]);
        }
      } else {
        // No current site yet - check if user has issuer role for ANY site
        hasPermission = user.site_roles?.some((role: any) => 
          role.role === 'issuer' || role.effective_permissions?.can_create_permits === true
        ) || Boolean(user[requirePermission as keyof typeof user]);
      }
    } else {
      // For other permissions, use global permission
      hasPermission = Boolean(user[requirePermission as keyof typeof user]);
    }
    
    if (!hasPermission) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  

  // Don't render anything while auth is loading
  if (isLoading) {
    return <div>Loading authentication...</div>;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        {/* Permit Routes */}
        <Route path="/permits" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_all_permits">
            <PermitList />
          </ProtectedRoute>
        } />
        <Route path="/permits/new" element={
          <ProtectedRoute requirePermission="can_create_permits">
            <PermitWizard />
          </ProtectedRoute>
        } />
        <Route path="/permits/create" element={
          <ProtectedRoute requirePermission="can_create_permits">
            <PermitForm />
          </ProtectedRoute>
        } />
        <Route path="/permits/comprehensive" element={
          <ProtectedRoute requirePermission="can_create_permits">
            <PermitComprehensiveForm />
          </ProtectedRoute>
        } />
        <Route path="/permits/access/new" element={
          <ProtectedRoute requirePermission="can_create_permits">
            <AccessPermitPage />
          </ProtectedRoute>
        } />
        <Route path="/permits/access/:id" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_all_permits">
            <AccessPermitPage />
          </ProtectedRoute>
        } />
        <Route path="/permits/:id" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_all_permits">
            <PermitDetail />
          </ProtectedRoute>
        } />
        
        {/* Station Log */}
        <Route path="/station-log" element={
          <ProtectedRoute>
            <StationLog />
          </ProtectedRoute>
        } />
        
        {/* Safety */}
        <Route path="/safety" element={
          <ProtectedRoute>
            <SafetyDashboard />
          </ProtectedRoute>
        } />
        
        {/* Cameras */}
        <Route path="/cameras" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_cameras">
            <CamerasDashboard />
          </ProtectedRoute>
        } />
        
        {/* Plant Information */}
        <Route path="/plant-information" element={
          <ProtectedRoute allowViewers={true}>
            <PlantInformation />
          </ProtectedRoute>
        } />
        
        {/* SCADA Historian */}
        <Route path="/historian" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_scada">
            <HistorianPage />
          </ProtectedRoute>
        } />
        
        {/* Alarm Management */}
        <Route path="/alarms" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_alarms">
            <AlarmDashboard />
          </ProtectedRoute>
        } />
        <Route path="/alarms/history" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_alarms">
            <AlarmHistory />
          </ProtectedRoute>
        } />
        
        {/* Documents */}
        <Route path="/documents" element={
          <ProtectedRoute allowViewers={true}>
            <DocumentsDashboard />
          </ProtectedRoute>
        } />
        
        {/* Planned Works */}
        <Route path="/planned-works" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_planned_work">
            <PlannedWorks />
          </ProtectedRoute>
        } />
        <Route path="/planned-works/:id" element={
          <ProtectedRoute allowViewers={true} requirePermission="can_view_planned_work">
            <PlannedWorkDetail />
          </ProtectedRoute>
        } />
        
        {/* Maintenance Routes */}
        <Route path="/maintenance" element={
          <ProtectedRoute>
            <EnhancedMaintenanceDashboard />
          </ProtectedRoute>
        } />
        <Route path="/maintenance/dashboard" element={
          <ProtectedRoute>
            <EnhancedMaintenanceDashboard />
          </ProtectedRoute>
        } />
        <Route path="/maintenance/legacy" element={
          <ProtectedRoute>
            <MaintenanceDashboard />
          </ProtectedRoute>
        } />
        <Route path="/maintenance/work-orders" element={
          <ProtectedRoute>
            <MaintenanceWorkOrders />
          </ProtectedRoute>
        } />
        <Route path="/maintenance/schedules" element={
          <ProtectedRoute>
            <MaintenanceSchedules />
          </ProtectedRoute>
        } />
        <Route path="/maintenance/routes" element={
          <ProtectedRoute>
            <MaintenanceRoutes />
          </ProtectedRoute>
        } />
        {/* Task definitions should be configured in backend/Django admin only */}
        {/* <Route path="/maintenance/task-definitions" element={
          <ProtectedRoute>
            <MaintenanceTaskDefinitions />
          </ProtectedRoute>
        } /> */}
        <Route path="/maintenance/standard-tasks" element={
          <ProtectedRoute>
            <StandardTaskManager />
          </ProtectedRoute>
        } />
        <Route path="/maintenance/records" element={
          <ProtectedRoute>
            <MaintenanceRecords />
          </ProtectedRoute>
        } />
        
        {/* Reports */}
        <Route path="/reports" element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } />
        
        {/* User Settings */}
        <Route path="/user-settings" element={
          <ProtectedRoute>
            <UserSettings />
          </ProtectedRoute>
        } />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <CssBaseline />
          <AuthProvider>
            <SiteProvider>
              <Router>
                <AppRoutes />
              </Router>
            </SiteProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App; 